import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FaStore, FaRupeeSign, FaBan } from 'react-icons/fa';
import { Page, PageHeader, Card, Badge, Modal, Field, Alert, Loading, auditStatus, formatMoney, formatDate } from '../../ui';
import { ServiceSummary, CustomerCard, VendorHistory, Findings } from '../../ui/JobParts';
import { api } from '../../ui/api';
import VendorPicker from './VendorPicker';

const QUOTE = { 0: ['Awaiting customer', 'amber'], 1: ['Accepted', 'green'], '-1': ['Declined', 'red'] };

const ManageAudit = () => {
  const { audit_id } = useParams();
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api(`/audit/get_audit/${audit_id}`).then(setAudit).catch((e) => setError(e.message));
  }, [audit_id]);
  useEffect(() => { load(); }, [load]);

  if (error && !audit) return <Page><Alert>{error}</Alert></Page>;
  if (!audit) return <Page><Loading /></Page>;

  const status = auditStatus(audit.status_code);
  const assignments = audit.vendor_assignment_list || [];
  const currentVendor = assignments.length ? assignments[assignments.length - 1].vendor : null;
  const code = audit.status_code;
  const canAssign = code === 0;
  const canChange = code > 0 && code < 4;
  const quoteDeclined = code === 5 && audit.final_price && audit.final_price.status_code === -1;
  const canPrice = (code === 4 && !Object.keys(audit.final_price || {}).length) || quoteDeclined;
  const canCancel = code >= 0 && code < 5;
  const findings = audit.audit_details && audit.audit_details.final_audit_details;
  const quote = audit.final_price && audit.final_price.final_price !== undefined ? audit.final_price : null;
  const quoteState = quote ? (QUOTE[quote.status_code] || QUOTE[0]) : null;

  const assign = async (vendor) => {
    const path = canAssign ? 'assign_vendor' : 'change_vendor';
    await api(`/audit/${path}/${audit_id}`, { method: 'POST', body: { vendor_id: vendor.id } });
    load();
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this audit? The customer will see it as cancelled.')) return;
    setBusy(true);
    try {
      await api(`/audit/cancel_audit/${audit_id}`, { method: 'PUT' });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitPrice = async () => {
    const value = parseFloat(price);
    if (Number.isNaN(value) || value <= 0) {
      setPriceError('Enter a price greater than zero.');
      return;
    }
    setBusy(true);
    try {
      await api(`/audit/submit_final_price/${audit_id}`, { method: 'POST', body: { final_price: value } });
      setPricing(false);
      load();
    } catch (e) {
      setPriceError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={`Audit ${audit.audit_id}`}
        subtitle={`Booked ${formatDate(audit.date_created)}`}
        back={{ to: '/audits', label: 'Audits' }}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />
      <Alert>{error}</Alert>

      <div className="grid grid-sidebar">
        <div className="stack">
          <ServiceSummary service={audit.service} date={audit.date} time={audit.time} address={audit.address} note={audit.comment} />
          <Findings title="Survey findings" items={findings} />
          <VendorHistory assignments={assignments} />
        </div>

        <div className="stack sticky-side side-first">
          {(canAssign || canChange || canPrice || canCancel) && (
            <Card title="Next step">
              <div className="action-list">
                {canAssign && <button className="btn btn-primary" onClick={() => setPicking(true)}><FaStore /> Assign vendor</button>}
                {canChange && <button className="btn btn-secondary" onClick={() => setPicking(true)}><FaStore /> Change vendor</button>}
                {canPrice && <button className="btn btn-primary" onClick={() => { setPrice(''); setPriceError(''); setPricing(true); }}><FaRupeeSign /> {quoteDeclined ? 'Send revised quote' : 'Set final price'}</button>}
                {canCancel && <button className="btn btn-danger-outline" disabled={busy} onClick={cancel}><FaBan /> Cancel audit</button>}
              </div>
            </Card>
          )}
          {quote && (
            <Card title="Quote">
              <div className="quote">
                <span className="price">{formatMoney(quote.final_price)}</span>
                <Badge tone={quoteState[1]}>{quoteState[0]}</Badge>
              </div>
            </Card>
          )}
          <CustomerCard user={audit.user} />
          {audit.payment && audit.payment.razorpay_payment_id && (
            <Card title="Audit fee">
              <dl className="kv one">
                <div><dt>Paid</dt><dd>{formatMoney((audit.payment.amount_paise || 0) / 100)}</dd></div>
                <div><dt>Payment ID</dt><dd className="mono">{audit.payment.razorpay_payment_id}</dd></div>
              </dl>
            </Card>
          )}
        </div>
      </div>

      <VendorPicker
        open={picking}
        title={canAssign ? 'Assign a vendor' : 'Change vendor'}
        currentVendorId={canChange && currentVendor ? currentVendor.id : null}
        onClose={() => setPicking(false)}
        onPick={assign}
      />

      <Modal
        open={pricing}
        title={quoteDeclined ? 'Send a revised quote' : 'Set final price'}
        subtitle={quoteDeclined ? `The customer declined ${formatMoney(audit.final_price.final_price)}.` : 'The customer is asked to accept this quote before the job is booked.'}
        onClose={() => setPricing(false)}
        onSubmit={submitPrice}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setPricing(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>Send quote</button>
          </>
        )}
      >
        <div className="form">
          <Alert>{priceError}</Alert>
          <Field label="Final price (₹)">
            <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} autoFocus placeholder="e.g. 2400" />
          </Field>
        </div>
      </Modal>
    </Page>
  );
};

export default ManageAudit;
